// Browser-local capability for zooi.launcher/1 (plugin/spec/launcher.md).
// It can start the fixed run skill; it grants no access to the zooi API.
export type LauncherHost = "claude" | "codex";
export interface LauncherPairing {
  schema: "zooi.launcher/1";
  id: string;
  key: string;
  product_id: string;
  origin: string;
  hosts: LauncherHost[];
  work_orders?: true;
  /** Fixed tasks the launcher can start by name (plugin 0.1.23 and later). */
  tasks?: LauncherTask[];
  /** Hosts whose desktop app opens instead of Terminal; the person sends the pre-filled skill there. */
  apps?: LauncherHost[];
}
export type LauncherTask = "data_setup";
const TASKS: readonly string[] = ["data_setup"];

export function parsePairing(text: string, productId: string, origin: string): LauncherPairing | null {
  try {
    if (text.length > 2048) return null;
    const value = JSON.parse(text) as LauncherPairing;
    if (!value || Object.keys(value).filter((key) => key !== "work_orders" && key !== "tasks" && key !== "apps").sort().join(",") !== "hosts,id,key,origin,product_id,schema" || value.schema !== "zooi.launcher/1"
      || (Object.hasOwn(value, "work_orders") && value.work_orders !== true)
      // Newer launchers may know more tasks; keep the ones this browser can ask for.
      || (Object.hasOwn(value, "tasks") && (!Array.isArray(value.tasks) || value.tasks.length > 8 || value.tasks.some((task) => typeof task !== "string" || !/^[a-z_]{1,32}$/.test(task))))
      || (Object.hasOwn(value, "apps") && (!Array.isArray(value.apps) || value.apps.length < 1 || new Set(value.apps).size !== value.apps.length || value.apps.some((host) => !value.hosts?.includes?.(host))))
      || typeof value.id !== "string" || !/^[a-f0-9]{32}$/.test(value.id) || typeof value.key !== "string" || !/^[a-f0-9]{64}$/.test(value.key)
      || value.product_id !== productId || value.origin !== origin || !Array.isArray(value.hosts) || value.hosts.length < 1 || value.hosts.length > 2
      || new Set(value.hosts).size !== value.hosts.length || value.hosts.some((host) => host !== "claude" && host !== "codex")) return null;
    if (value.tasks) value.tasks = value.tasks.filter((task) => TASKS.includes(task));
    return value;
  } catch { return null; }
}

/** The browser cannot supply a directory, executable, prompt or extra CLI arguments. */
export function launcherHref(pairing: LauncherPairing, host: LauncherHost, workOrderId?: string, task?: LauncherTask): string {
  if (!pairing.hosts.includes(host)) throw new Error("launcher_host_invalid");
  if (workOrderId !== undefined && (!pairing.work_orders || !/^wko_[a-z0-9]{1,64}$/.test(workOrderId))) throw new Error("launcher_work_order_invalid");
  if (task !== undefined && (workOrderId !== undefined || !pairing.tasks?.includes(task))) throw new Error("launcher_task_invalid");
  return `zooi://run/${pairing.id}?${new URLSearchParams({ key: pairing.key, host, ...(workOrderId ? { work_order: workOrderId } : {}), ...(task ? { task } : {}) })}`;
}

/** Where a launch for this host shows up on this computer. */
export const launchSurface = (pairing: LauncherPairing, host: LauncherHost): "app" | "terminal" => pairing.apps?.includes(host) ? "app" : "terminal";

export const pairingStorageKey = (scope: string, productId: string) => `zooi:launcher:${scope}:${productId}`;
export const PAIRING_EVENT = "zooi:launcher-changed";

export function readPairing(scope: string, productId: string): string | null {
  try { return localStorage.getItem(pairingStorageKey(scope, productId)); } catch { return null; }
}

export function writePairing(scope: string, productId: string, pairing: LauncherPairing | null): boolean {
  try {
    const key = pairingStorageKey(scope, productId);
    if (pairing) localStorage.setItem(key, JSON.stringify(pairing));
    else {
      localStorage.removeItem(key);
      localStorage.removeItem(setupStorageKey(scope, productId));
    }
    window.dispatchEvent(new Event(PAIRING_EVENT));
    return true;
  } catch { return false; }
}

export function subscribePairing(listener: () => void) {
  window.addEventListener(PAIRING_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => { window.removeEventListener(PAIRING_EVENT, listener); window.removeEventListener("storage", listener); };
}

const setupStorageKey = (scope: string, productId: string) => `zooi:launcher-setup:${scope}:${productId}`;
export const SETUP_LIFETIME = 30 * 60 * 1000;

/** A short-lived return address, not a launch capability. It may appear in the setup prompt. */
export function beginLauncherSetup(scope: string, productId: string, now = Date.now()): string | null {
  try {
    const id = crypto.randomUUID().replaceAll("-", "");
    localStorage.setItem(setupStorageKey(scope, productId), JSON.stringify({ id, at: now }));
    return id;
  } catch { return null; }
}

/** Old installers still deliver just a pairing; those returns require one confirmation. */
export function pairingReturn(hash: string): { code: string; setupId: string | null } | null {
  if (!hash.startsWith("#zooi-launcher=") || hash.length > 8192) return null;
  try {
    const params = new URLSearchParams(hash.slice(1));
    if (params.getAll("zooi-launcher").length !== 1 || params.getAll("setup").length > 1) return null;
    return { code: params.get("zooi-launcher") ?? "", setupId: params.get("setup") };
  } catch { return null; }
}

/** Only a return initiated by this person, browser and product can be saved automatically. */
export function completeLauncherSetup(scope: string, productId: string, code: string, origin: string, setupId: string | null, now = Date.now()): boolean {
  try {
    const pairing = parsePairing(code, productId, origin);
    if (!pairing) return false;
    if (!setupId || !/^[a-f0-9]{32}$/.test(setupId)) return false;
    const key = setupStorageKey(scope, productId);
    const pending = JSON.parse(localStorage.getItem(key) ?? "null");
    if (!pending || pending.id !== setupId || !Number.isFinite(pending.at) || now < pending.at || now - pending.at > SETUP_LIFETIME) return false;
    if (!writePairing(scope, productId, pairing)) return false;
    localStorage.removeItem(key);
    return true;
  } catch { return false; }
}
